import { ContentCopyRounded, DeleteRounded, ExpandMore, Person, SearchRounded, WarningRounded } from "@mui/icons-material";
import { Accordion, AccordionDetails, AccordionSummary, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControlLabel, Grid, IconButton, Radio, Stack, Tooltip, Typography } from "@mui/material";
import React, { useCallback, useContext, useState } from "react";
import { useFormContext, useWatch, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useRecoilState, useRecoilValue } from "recoil";
import { useQueryClient } from "@tanstack/react-query";
import _ from "underscore";
import { IFormValues } from "../../../types";
import { CreditType, FirstInvoiceRecipient, ICustomer, IMobilePrefix, InvoiceType, ItCodeCountry, LegalFormCode, ReactQueryKey } from "../../../../../types";
import { FormDatePicker, FormRadioGroup, FormTextField } from "../../../../../components/fields";
import { ICompany, IGetCompanyReturn, IGetCustomersParams } from "../../../../../api/services";
import { GlobalContext } from "../../../../../../../util/state/GlobalContextProvider";
import { editAccordionExpandedState, editCustomerBillingDataB2B } from "../../../recoil";
import useDialog from "../../../../../hooks/useDialog";
import { setSnackbar } from "../../../../../../../util/state/globalAction";
import { snackbarTypes } from "../../../../../../../util/state/globalVariables";
import DialogSearchCustomer from "../../../../../components/DialogSearchCustomer";
import GoogleAutocompleteWrapper from "../../../../../../../util/component/GoogleAutocompleteWrapper";
import {
    clearFieldInvoice,
    getRequiredVatNr,
    setInvoicePropertyCompanyByICompany,
    setInvoicePropertyCompanyByICustomer,
    setInvoicePropertyPersonCustomer
} from "../../../../../utils";
import { getCustomer } from "../../../../../api/services";
import DialogCloseButton from "../../../../../components/DialogCloseButton";
import ResidenceCountryField from "./_component/ResidenceCountryField";
import MobilePrefixField from "./_component/MobilePrefixField";
import BillingCountryField from "./_component/BillingCountryField";
import LegalFormField from "./_component/LegalFormField";
import BirthNationField from "./_component/BirthNationField";
import BillingProvinceField from "./_component/BillingProvinceField";
import VatNumberCountry from "./_component/VatNumberCountry";

const InvoiceDetails = () => {
    const { dispatchGlobalData } = useContext(GlobalContext);

    const { t } = useTranslation('booking');

    const [invoiceExpanded, setInvoiceExpanded] = useRecoilState(editAccordionExpandedState('invoice'));
        const customerBillingData = useRecoilValue(editCustomerBillingDataB2B);

    const queryClient = useQueryClient();

    const formContext = useFormContext<IFormValues>();

    const rentalType = useWatch<IFormValues, "basicDetails.rentalType">({
        name: 'basicDetails.rentalType'
    });
    const invoiceType = useWatch<IFormValues, "invoice.type">({
        name: 'invoice.type'
    });
    const invoiceId = useWatch<IFormValues, "invoice.id">({
        name: 'invoice.id'
    });
    const invoiceUUID = useWatch<IFormValues, "invoice.uuid">({
        name: 'invoice.uuid'
    });

    const searchDialog = useDialog();
    const selectCustomerErrorDialog = useDialog();

    const [invoiceFilter, setInvoiceFilter] = useState<IGetCustomersParams['filters'] | null>(null);

    const [billingCountryDisabled, setBillingCountryDisabled] = useState<boolean>(true);
    const [billingProvinceDisabled, setBillingProvinceDisabled] = useState<boolean>(true);
    const [billingCityDisabled, setBillingCityDisabled] = useState<boolean>(true);
    const [billingStreetNameDisabled, setBillingStreetNameDisabled] = useState<boolean>(true);
    const [billingStreetNumberDisabled, setBillingStreetNumberDisabled] = useState<boolean>(true);
    const [billingPostalCodeDisabled, setBillingPostalCodeDisabled] = useState<boolean>(true);
    const [birthNationDisabled, setBirthNationDisabled] = useState<boolean>(true); // todo se confermato eliminare stato nell'invoice form

    const productAffiliatedQuery = queryClient.getQueryData<IGetCompanyReturn>([ReactQueryKey.Product, {segment: rentalType}]);

    const NoCreditCondition = productAffiliatedQuery?.creditType === CreditType.NO_CREDIT;
    const CompanyCondition = productAffiliatedQuery?.firstInvoiceRecipient !== FirstInvoiceRecipient.CUSTOMER && productAffiliatedQuery?.firstInvoiceRecipient !== FirstInvoiceRecipient.PERSON;
    const codeLegalEntity = customerBillingData?.legalEntity?.code;

    const googleInputPropsBirthField = {
        onPlaceSelected: (place: any) => selectedBirthCountry(place),
        options: {
            fields: ['address_components']
        }
    }
    const googleInputPropsBillingField = {
        onPlaceSelected: (place: any) => selectedBillingAddress(place),
        options: {
            fields: ['address_components'],
            types: ['address']
        }
    }

    // action copy by firstDriver
    const handleCopy = useCallback((e: React.SyntheticEvent) => {
        e.stopPropagation();
        const firstDriver = formContext.getValues('firstDriver');

        for (const [key, value] of Object.entries(firstDriver)) {
            // @ts-ignore
            formContext.setValue((`invoice.${key}`), value);
        }
    }, []);

    // action search customer
    const handleSearch = useCallback((e: React.SyntheticEvent) => {
        e.stopPropagation();
        const invoice = formContext.getValues('invoice');
        if (invoice.type === InvoiceType.PERSON && invoice.birthDate && (!invoice.name || !invoice.surname)) {
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t('message.error.first_driver_search_required_fields'),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true,
                    }
                }
            })
            return;
        }

        if (invoice.type !== InvoiceType.PERSON && !invoice.companyName && !invoice.vatNumber){
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t('message.error.billing_company_owner_search_required_fields'),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true,
                    }
                }
            })
            return;
        }
        switch (invoice.type) {
            case InvoiceType.PERSON:
                setInvoiceFilter({
                    firstName: invoice.name,
                    lastName: invoice.surname,
                    birthDate: invoice.birthDate?.format('YYYY-MM-DD') || '',
                    drivingLicenseNumber: invoice.drivingLicenseNumber,
                    fiscalCode: invoice.fiscalCode
                });
                break;
            case InvoiceType.VAT_NUMBER_HOLDER:
                setInvoiceFilter({
                    companyName: invoice.companyName,
                    vatNumber: invoice.vatNumber,
                });
                break;
            case InvoiceType.COMPANY:
                setInvoiceFilter({
                    companyName: invoice.companyName,
                    vatNumber: invoice.vatNumber,
                });
        }
        searchDialog.handleShow();
    }, [t]);

    // action selected customer
    const handleCustomerSelected = useCallback((selectedCustomer: ICustomer) => {
        switch(formContext.getValues('invoice.type')) {
            case InvoiceType.PERSON:
                for (const [key, value] of Object.entries(setInvoicePropertyPersonCustomer(selectedCustomer))) {
                    // @ts-ignore
                    formContext.setValue((`${key}`), value);
                }

                const mobilePrefixes = queryClient.getQueryData<IMobilePrefix[]>([ReactQueryKey.MobilePrefixes]);
                const foundMobilePrefix = mobilePrefixes?.find(mp => mp.prefix === selectedCustomer.mobilePrefix);
                if (foundMobilePrefix) {
                    formContext.setValue('invoice.mobilePrefix', foundMobilePrefix);
                }
                break;
            case InvoiceType.COMPANY:
            case InvoiceType.VAT_NUMBER_HOLDER:
                if(!_.isEmpty(selectedCustomer?.legalEntity)) {
                    if( (selectedCustomer.legalEntity?.code === LegalFormCode.DITTA_INDIVIDUALE && formContext.getValues('invoice.type') === InvoiceType.COMPANY) ||
                        (selectedCustomer.legalEntity?.code !== LegalFormCode.DITTA_INDIVIDUALE && formContext.getValues('invoice.type') === InvoiceType.VAT_NUMBER_HOLDER) ){
                        selectCustomerErrorDialog.handleShow();
                        return;
                    }
                }
                for (const [key, value] of Object.entries(setInvoicePropertyCompanyByICustomer(selectedCustomer))) {
                    // @ts-ignore
                    formContext.setValue((`${key}`), value);
                }

                break;
            }
    }, []);

    // action reset form field
    const handleDeleteClick = useCallback((e: React.SyntheticEvent) => {
        e.stopPropagation();
        formContext.resetField('invoice');

        setBirthNationDisabled(false);
        resetBillingFieldDisabled();
    }, []);

    // reset disabled form field Billing
    const resetBillingFieldDisabled = () => {
        setBillingCountryDisabled(true)
        setBillingCityDisabled(true);
        setBillingStreetNameDisabled(true);
        setBillingStreetNumberDisabled(true);
        setBillingPostalCodeDisabled(true);
        setBillingProvinceDisabled(true);
    }

    // Selected billing address and set value form
    const selectedBillingAddress = useCallback((result : any) => {
        if(!result){
            return;
        }

        const country = result?.address_components?.find((elem: any) => elem.types.includes('country'));
        const locality = result?.address_components?.find((elem: any) => elem?.types.includes('locality'));
        const streetName = result?.address_components?.find((elem: any) => elem.types.includes('route'));
        const streetNumber = result?.address_components?.find((elem: any) => elem.types.includes('street_number'));
        const postalCode = result?.address_components?.find((elem: any) => elem.types.includes('postal_code'));

        // set value form for billing data
        formContext.setValue('invoice.billingStreetNumber', !!streetNumber ? streetNumber.short_name : '');
        formContext.setValue('invoice.billingCountry',  country?.short_name ?? '');
        formContext.setValue('invoice.billingCity', locality?.short_name ?? '');
        formContext.setValue('invoice.billingStreetName',  streetName?.short_name ?? '');
        formContext.setValue('invoice.billingStreetNumber', streetNumber?.short_name ?? '');
        if(!!streetName) {
            formContext.setValue('invoice.billingStreetNameGoogleSearch', streetName.short_name);
        }

        formContext.setValue('invoice.billingPostalCode', postalCode?.short_name ?? '');

        setBillingCountryDisabled(!!country);
        setBillingCityDisabled(!!locality);
        setBillingStreetNameDisabled(!!streetName);
        setBillingStreetNumberDisabled(!!streetNumber);
        setBillingPostalCodeDisabled(!!postalCode);
        // for IT get data fro api Google
        if(country?.short_name === ItCodeCountry) {
            const province = result?.address_components.find((elem: any) => elem.types.includes('administrative_area_level_2'));
            formContext.setValue('invoice.billingProvince',  province?.short_name ?? '');
            setBillingProvinceDisabled(!!province);
        } else {
            // for foreign set province from Api rest
            formContext.setValue('invoice.billingProvince',  '');
            setBillingProvinceDisabled(false);
        }
    }, []);

     // Selected billing address and set value form
    const selectedBirthCountry = useCallback((result: any) => {

        const country = result?.address_components?.find((elem: any) => elem.types.includes('country'));
        const locality = result?.address_components?.find((elem: any) => elem?.types.includes('locality'));

        formContext.setValue('invoice.birthPlace', locality?.short_name ?? '');
        formContext.setValue('invoice.birthNation', country?.short_name ?? '');
        setBirthNationDisabled(!!country);
    }, []);

    // reset value billing address data
    const handleResetBillingAddressFields = useCallback(() => {
        formContext.resetField('invoice.billingStreetNameGoogleSearch');
        resetBillingAddress()
    }, []);

    // Reset value billing address field
    const resetBillingAddress = () => {
        formContext.setValue('invoice.billingCountry', '');
        formContext.setValue('invoice.billingProvince', '');
        formContext.setValue('invoice.billingCity', '');
        formContext.setValue('invoice.billingStreetName', '');
        formContext.setValue('invoice.billingStreetNumber', '');
        formContext.setValue('invoice.billingPostalCode', '');

        resetBillingFieldDisabled();
    }

    // Reset field birth nation
    const handleResetBirthNation = () => {
        formContext.setValue('invoice.birthNation', '');
        setBirthNationDisabled(true);
    }

    // Reset invoice form for change selection type radio group
    const resetInvoiceForm = async () => {
        formContext.clearErrors('invoice');

        const type = formContext.getValues('invoice.type');
        // clean other fields value
        for (const [key, value] of Object.entries(clearFieldInvoice())) {
            // @ts-ignore
            formContext.setValue((`${key}`), value);
        }

        setBirthNationDisabled(false);
        resetBillingFieldDisabled();
        formContext.setValue("invoice.type", type);

        if(type === InvoiceType.PERSON) {
            const fistDriverId = formContext.getValues('firstDriver.id');
            if(!!fistDriverId) {
                let customer = await getCustomer(fistDriverId);
                for (const [key, value] of Object.entries(setInvoicePropertyPersonCustomer(customer))) {
                    // @ts-ignore
                    formContext.setValue((`${key}`), value);
                }

                formContext.setValue('invoice.email', formContext.getValues('firstDriver.email'));
                formContext.setValue('invoice.mobile', formContext.getValues('firstDriver.mobile'));
                formContext.setValue('invoice.mobilePrefix', formContext.getValues('firstDriver.mobilePrefix'));
            } else {
                for (const [key, value] of Object.entries(formContext.getValues('firstDriver'))) {
                    // @ts-ignore
                    formContext.setValue((`invoice.${key}`), value);
                }
            }
        } else {
            const billingOwnerCompany = setInvoicePropertyCompanyByICompany(customerBillingData as ICompany);
            for (const [key, value] of Object.entries(billingOwnerCompany)) {
                // @ts-ignore
                formContext.setValue((`${key}`), value);
            }
        }
    }

    return (
        <>
            <DialogSearchCustomer
                section={'invoice'}
                open={searchDialog.open}
                onClose={searchDialog.handleClose}
                onCustomerSelected={handleCustomerSelected}
                customersFilter={invoiceFilter}
            />
            <Dialog
                open={selectCustomerErrorDialog.open}
                onClose={selectCustomerErrorDialog.handleClose}
                fullWidth
                maxWidth="sm"
            >
                <DialogCloseButton
                    onClick={selectCustomerErrorDialog.handleClose}
                />
                <DialogTitle>
                    <Stack
                        direction="row"
                        alignItems="center"
                        columnGap={1}
                    >
                        <WarningRounded color="error" />
                        {t('label.error')}
                    </Stack>
                </DialogTitle>
                <DialogContent>
                    <DialogContentText
                        sx={{
                            whiteSpace: 'pre-line'
                        }}
                    >
                        {t('message.error.illegal_legal_form_for_billing_type')}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={selectCustomerErrorDialog.handleClose}
                    >
                        {t('label.ok')}
                    </Button>
                </DialogActions>
            </Dialog>
            <Accordion expanded={invoiceExpanded} onChange={(e, expanded) => setInvoiceExpanded(expanded)}>
                <AccordionSummary expandIcon={<ExpandMore/>} sx={{
                    '.MuiAccordionSummary-content': {
                        justifyContent: "space-between",
                        alignItems: "center"
                    }
                }}>
                    <Stack
                        direction="row"
                        alignItems="center"
                        columnGap={2}
                    >
                        <Typography variant="h5">
                            {t('label.billingOwner')}
                        </Typography>
                        {invoiceId &&
                            <Person color="secondary"/>
                        }
                    </Stack>
                    <Stack direction="row" mr={2} alignItems="center">
                        <IconButton onClick={handleCopy}
                                    disabled={true}>
                            <ContentCopyRounded fontSize="small"/>
                        </IconButton>
                        <IconButton onClick={handleSearch}
                                    disabled={true}>
                            <SearchRounded fontSize="small"/>
                        </IconButton>
                        <IconButton onClick={handleDeleteClick}
                                    disabled={true}>
                            <DeleteRounded fontSize="small"/>
                        </IconButton>
                    </Stack>
                </AccordionSummary>
                <AccordionDetails>
                    <FormRadioGroup<IFormValues>
                        name="invoice.type"
                        label=""
                        onChange={resetInvoiceForm}
                    >
                        <FormControlLabel
                            value={InvoiceType.PERSON}
                            label={t('label.person')}
                            control={<Radio/>}
                            disabled={ !NoCreditCondition || (NoCreditCondition && CompanyCondition) || (NoCreditCondition && !codeLegalEntity)} //Ultimo caso: BA persona fisica caso No Credit (si fattura su Customer)
                        />
                        <FormControlLabel
                            value={InvoiceType.VAT_NUMBER_HOLDER}
                            label={t('label.vatNumberHolder')}
                            control={<Radio/>}
                            disabled={ !NoCreditCondition || (NoCreditCondition && codeLegalEntity !== LegalFormCode.DITTA_INDIVIDUALE)}
                        />
                        <FormControlLabel
                            value={InvoiceType.COMPANY}
                            label={t('label.company')}
                            control={<Radio/>}
                            disabled={ !NoCreditCondition  || (NoCreditCondition && codeLegalEntity === LegalFormCode.DITTA_INDIVIDUALE) || (NoCreditCondition && !codeLegalEntity)}
                        />
                    </FormRadioGroup>
                    {formContext.getValues('invoice.type') === InvoiceType.PERSON &&
                        <Grid container rowSpacing={1} columnSpacing={3}>

                            <Grid item xs={12} sm={6}>
                                <FormTextField<IFormValues>
                                    name="invoice.name"
                                    label={t('label.firstName')}
                                    disabled={true}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <FormTextField<IFormValues>
                                name="invoice.surname"
                                label={t('label.lastName')}
                                disabled={true}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormDatePicker<IFormValues>
                                    name="invoice.birthDate"
                                    label={t('label.dateOfBirth')}
                                    disableFuture
                                    disabled={true}
                                />
                            </Grid>
                           {/* Api google country and locality birth */}
                            <Grid item xs={12} sm={6}>
                                <Controller
                                    name="invoice.birthPlace"
                                    control={formContext.control}
                                    render={({field: {onChange, value}}: any) => (
                                        <GoogleAutocompleteWrapper
                                            googleInputProps={googleInputPropsBirthField}
                                            inputProps={
                                                {
                                                    fullWidth: true,
                                                    label: t('label.cityOfBirth') + (invoiceType === InvoiceType.VAT_NUMBER_HOLDER ? '*' : ''),
                                                    id: 'birthPlace',
                                                    name: 'invoice.birthPlace',
                                                    type: 'text',
                                                    value: value || '',
                                                    onChange: (e) => {
                                                        onChange(e.target.value)
                                                        if (!e.target.value) {
                                                            handleResetBirthNation()
                                                        }
                                                    },
                                                    disabled: true
                                                }}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <BirthNationField birthNationDisabled={true}/>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <ResidenceCountryField/>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormTextField<IFormValues>
                                    name="invoice.fiscalCode"
                                    label={t('label.fiscalCode')}
                                    disabled={true}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormTextField<IFormValues>
                                    name="invoice.email"
                                    label={t('label.email')}
                                    disabled={true}
                                />
                            </Grid>
                            <Grid container item xs={12} sm={6} columnSpacing={1}>
                                <Grid item width={150}>
                                    <MobilePrefixField/>
                                </Grid>
                                <Grid item xs>
                                    <FormTextField<IFormValues>
                                        name="invoice.mobile"
                                        label={t('label.mobile')}
                                        disabled={true}
                                    />
                                </Grid>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormTextField<IFormValues>
                                    name="invoice.drivingLicenseNumber"
                                    label={t('label.drivingLicenseNumber')}
                                    disabled={true}
                                />
                            </Grid>
                            {/* api google for billing address*/}
                            <Grid item xs={10} sm={10}>
                                <Controller
                                    name="invoice.billingStreetNameGoogleSearch"
                                    control={formContext.control}
                                    render={({field: {onChange, value}}: any) => (

                                        <GoogleAutocompleteWrapper
                                            googleInputProps={googleInputPropsBillingField}
                                            inputProps={
                                                {
                                                    fullWidth: true,
                                                    label: t('label.billingStreetName'),
                                                    id: 'billingStreetNameGoogleSearch',
                                                    name: 'invoice.billingStreetNameGoogleSearch',
                                                    type: 'text',
                                                    value: value || '',
                                                    onChange: (e) => {
                                                        onChange(e.target.value)
                                                        if (!e.target.value) {
                                                            handleResetBillingAddressFields()
                                                        }
                                                    },
                                                }
                                            }
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={2} sm={2} alignItems="center"
                                  sx={{textAlign: 'center', marginTop: 'auto'}}>
                                <Tooltip arrow placement={"top"} title={t("label.reset")}>
                                    <IconButton onClick={handleResetBillingAddressFields}>
                                        <DeleteRounded fontSize="small"/>
                                    </IconButton>
                                </Tooltip>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <BillingCountryField
                                    billingCountryDisabled={billingCountryDisabled}/>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <BillingProvinceField
                                    billingProvinceDisabled={billingProvinceDisabled}/>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormTextField<IFormValues>
                                    name="invoice.billingCity"
                                    label={t('label.billingCity')}
                                    disabled={billingCityDisabled}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormTextField<IFormValues>
                                    name="invoice.billingStreetName"
                                    label={t('label.billingStreetName')}
                                    disabled={billingStreetNameDisabled}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormTextField<IFormValues>
                                    name="invoice.billingStreetNumber"
                                    label={t('label.billingStreetNumber')}
                                    disabled={billingStreetNumberDisabled}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormTextField<IFormValues>
                                    name="invoice.billingPostalCode"
                                    label={t('label.billingPostalCode')}
                                    disabled={billingPostalCodeDisabled}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormTextField<IFormValues>
                                    name="invoice.certifiedEmail"
                                    label={t('label.certifiedEmailAddress')}
                                />
                            </Grid>


                        </Grid>
                    }
                    {formContext.getValues('invoice.type') !== InvoiceType.PERSON &&
                    <Grid container rowSpacing={1} columnSpacing={3}>
                        <Grid item xs={12} sm={6}>
                            <LegalFormField />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormTextField<IFormValues>
                                name="invoice.companyName"
                                label={t('label.companyName')}
                                required
                                disabled={!!invoiceId || formContext.getValues('invoice.linkedToBusinessAgreement') || !NoCreditCondition || (NoCreditCondition && CompanyCondition)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormTextField<IFormValues>
                                name="invoice.fiscalCode"
                                label={t('label.fiscalCode')}
                                required
                                disabled={!!invoiceId || formContext.getValues('invoice.linkedToBusinessAgreement') || !NoCreditCondition || (NoCreditCondition && CompanyCondition)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormTextField<IFormValues>
                                name="invoice.email"
                                label={t('label.email') + '*'}
                                disabled={!!invoiceUUID || formContext.getValues('invoice.linkedToBusinessAgreement') || !NoCreditCondition || (NoCreditCondition && CompanyCondition)}
                            />
                        </Grid>
                        {/* api google for billing address*/}
                        <Grid item xs={10} sm={10}>
                            <Controller
                                name="invoice.billingStreetNameGoogleSearch"
                                control={formContext.control}
                                render={({field: {onChange, value}}: any) => (
                                    <GoogleAutocompleteWrapper
                                        googleInputProps={googleInputPropsBillingField}
                                        inputProps={
                                            {
                                                disabled: formContext.getValues('invoice.linkedToBusinessAgreement') || !NoCreditCondition || (NoCreditCondition && CompanyCondition),
                                                fullWidth: true,
                                                label: t('label.billingStreetName'),
                                                id: 'billingStreetNameGoogleSearch',
                                                name: 'invoice.billingStreetNameGoogleSearch',
                                                type: 'text',
                                                value: value || '',
                                                onChange: (e) => {
                                                    onChange(e.target.value)
                                                    if (!e.target.value) {
                                                        handleResetBillingAddressFields()
                                                    }
                                                },
                                            }
                                        }
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={2} sm={2} alignItems="center"
                              sx={{textAlign: 'center', marginTop: 'auto'}}>
                            <Tooltip arrow placement={"top"} title={t("label.reset")}>
                                <IconButton onClick={handleResetBillingAddressFields}
                                            disabled={formContext.getValues('invoice.linkedToBusinessAgreement') || !NoCreditCondition || (NoCreditCondition && CompanyCondition)}>
                                    <DeleteRounded fontSize="small"/>
                                </IconButton>
                            </Tooltip>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <BillingCountryField
                                billingCountryDisabled={billingCountryDisabled || formContext.getValues('invoice.linkedToBusinessAgreement') || !NoCreditCondition || (NoCreditCondition && CompanyCondition)}/>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <BillingProvinceField
                                billingProvinceDisabled={billingProvinceDisabled || formContext.getValues('invoice.linkedToBusinessAgreement') || !NoCreditCondition || (NoCreditCondition && CompanyCondition)}/>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormTextField<IFormValues>
                                name="invoice.billingCity"
                                label={t('label.billingCity') + '*'}
                                disabled={billingCityDisabled || formContext.getValues('invoice.linkedToBusinessAgreement') || !NoCreditCondition || (NoCreditCondition && CompanyCondition)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormTextField<IFormValues>
                                name="invoice.billingStreetName"
                                label={t('label.billingStreetName') + '*'}
                                disabled={billingStreetNameDisabled || formContext.getValues('invoice.linkedToBusinessAgreement') || !NoCreditCondition || (NoCreditCondition && CompanyCondition)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormTextField<IFormValues>
                                name="invoice.billingStreetNumber"
                                label={t('label.billingStreetNumber') + '*'}
                                disabled={billingStreetNumberDisabled || formContext.getValues('invoice.linkedToBusinessAgreement') || !NoCreditCondition || (NoCreditCondition && CompanyCondition)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormTextField<IFormValues>
                                name="invoice.billingPostalCode"
                                label={t('label.billingPostalCode') + '*'}
                                disabled={billingPostalCodeDisabled || formContext.getValues('invoice.linkedToBusinessAgreement') || !NoCreditCondition || (NoCreditCondition && CompanyCondition)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormTextField<IFormValues>
                                name="invoice.certifiedEmail"
                                label={t('label.certifiedEmailAddress')}
                                disabled={formContext.getValues('invoice.linkedToBusinessAgreement') || !NoCreditCondition || (NoCreditCondition && CompanyCondition)}
                            />
                        </Grid>
                        {formContext.getValues('invoice.type') === InvoiceType.COMPANY && !formContext.getValues('invoice.publicAuthority') &&
                        <Grid item xs={12} sm={6}>
                            <FormTextField<IFormValues>
                                name="invoice.invoiceCode"
                                label={t('label.invoiceCode')}
                                disabled={formContext.getValues('invoice.linkedToBusinessAgreement') || !NoCreditCondition || (NoCreditCondition && CompanyCondition)}
                            />
                        </Grid>
                        }
                        {formContext.getValues('invoice.type') === InvoiceType.COMPANY && formContext.getValues('invoice.publicAuthority') &&
                            <Grid item xs={12} sm={6}>
                                <FormTextField<IFormValues>
                                name="bookingOwner.paIdNumber"
                                label={t('label.paIdNumber')}
                                disabled={formContext.getValues('invoice.linkedToBusinessAgreement') || !NoCreditCondition || (NoCreditCondition && CompanyCondition)}
                                />
                            </Grid>
                        }

                        <Grid item xs={12} sm={6}>
                            <VatNumberCountry
                                disabled={!!invoiceId || formContext.getValues('invoice.linkedToBusinessAgreement') || !NoCreditCondition || (NoCreditCondition && CompanyCondition)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormTextField<IFormValues>
                                name="invoice.vatNumber"
                                label={t('label.vatNumber') + (getRequiredVatNr(formContext.getValues('invoice.legalEntity')?.code) ? '*' : '')}
                                disabled={!!invoiceId || formContext.getValues('invoice.linkedToBusinessAgreement') || !NoCreditCondition || (NoCreditCondition && CompanyCondition)}
                            />
                        </Grid>
                    </Grid>
                    }
                </AccordionDetails>
            </Accordion>
        </>
    );
};

export default InvoiceDetails;
